import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const PresidentMessage = () => {
  const [showLogoPopup, setShowLogoPopup] = useState(false);

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Message from the President
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full"></div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-card rounded-2xl card-shadow p-8 lg:p-12"
          >
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-start">
              {/* President Profile Section */}
              <div className="lg:col-span-1 text-center lg:text-left">
                <div className="relative inline-block mb-4">
                  <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full overflow-hidden ring-2 ring-primary/20 mx-auto lg:mx-0">
                    <img
                      src="/assets/images/members/cp_profile.jpg"
                      alt="Leo Vidurshanan Shakthi - Club President"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="Crown" size={14} className="text-white" />
                  </div>
                </div>

                <div className="mb-4">
                  <h3 className="text-lg lg:text-xl font-bold text-foreground mb-1">
                    Leo Vidurshanan Shakthi
                  </h3>
                  <p className="text-primary font-semibold text-sm mb-1">Club President</p>
                  <p className="text-muted-foreground text-xs">2025 / 2026</p>
                  <p className="text-muted-foreground text-xs font-medium">
                    Leo Club of Colombo Circle
                  </p>
                </div>

                {/* Club Logo */}
                <div className="flex justify-center lg:justify-start mt-6">
                  <div
                    className="cursor-pointer transform hover:scale-105 transition-transform duration-300"
                    onClick={() => setShowLogoPopup(true)}
                  >
                    <img
                      src="/assets/images/club_logo_sm.png"
                      alt="Leo Club of Colombo Circle"
                      className="h-48 lg:h-56 w-auto opacity-95 drop-shadow-lg hover:opacity-100"
                    />
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div className="lg:col-span-2">
                <div className="relative">
                  {/* Quote Icon */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Quote" size={24} className="text-primary" />
                  </div>

                  <div className="pl-8">
                    {/* Theme Quote */}
                    <div className="mb-6 p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                      <p className="text-lg font-semibold text-primary italic">
                        "Youth of today are the leaders of tomorrow."
                      </p>
                    </div>

                    {/* Main Message */}
                    <div className="prose max-w-none text-muted-foreground leading-relaxed">
                      <p className="mb-3 text-sm lg:text-base">
                        Leoism is a platform filled with many opportunities and endless resources for enthusiastic and passionate young leaders who have the vision to serve the society while developing themselves. Becoming a leo was probably one of the best decisions I've taken in my life.
                      </p>

                      <p className="mb-3 text-sm lg:text-base">
                        I would like to thank all the Leos and Lions who have helped in various ways to reach the pinnacle of my leoistic career.
                      </p>

                      {/* Theme Highlight */}
                      <div className="my-4 p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                        <div className="flex items-center mb-2">
                          <Icon name="Target" size={20} className="text-primary mr-2" />
                          <h4 className="text-lg font-bold text-foreground">Theme for 2025/2026</h4>
                        </div>
                        <p className="text-xl font-bold text-primary mb-2">"Rise to be Strong"</p>
                        <p className="text-muted-foreground text-sm">
                          To achieve this, I have set up a year plan with various projects to focus on existing members' integrity and to identify devoted young people who realize the power of action.
                        </p>
                      </div>

                      <p className="mb-3 text-sm lg:text-base">
                        I believe that we will raise the standards, not alone, but together. I would like to congratulate and wish all the very best to the club officers and all the club members who are the most vital part of our club.
                      </p>

                      <p className="font-semibold text-foreground text-sm lg:text-base">
                        I'm certain that I will obtain your fullest support and cooperation in achieving our goals this year.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="mt-8 pt-8 border-t border-border text-center"
            >
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium">
                  <Icon name="Users" size={16} className="mr-2" />
                  Join Our Mission
                </div>
                <div className="flex items-center px-4 py-2 bg-accent/10 rounded-full text-accent text-sm font-medium">
                  <Icon name="Heart" size={16} className="mr-2" />
                  Serve the Community
                </div>
                <div className="flex items-center px-4 py-2 bg-secondary/10 rounded-full text-secondary text-sm font-medium">
                  <Icon name="Zap" size={16} className="mr-2" />
                  Rise to be Strong
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Logo Popup Modal */}
      <AnimatePresence>
        {showLogoPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLogoPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-card rounded-2xl p-8 lg:p-12 max-w-4xl w-full max-h-[90vh] overflow-y-auto card-shadow-hover"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowLogoPopup(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-muted hover:bg-muted-foreground/20 rounded-full flex items-center justify-center transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Large Logo */}
                <div className="text-center">
                  <img
                    src="/assets/images/club_logo_sm.png"
                    alt="Leo Club of Colombo Circle"
                    className="w-full max-w-md mx-auto h-auto drop-shadow-2xl"
                  />
                  <div className="mt-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      Leo Club of Colombo Circle
                    </h3>
                    <p className="text-muted-foreground">
                      Empowering Youth • Serving Community • Building Leaders
                    </p>
                  </div>
                </div>

                {/* Theme Information */}
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                      Logo and Theme
                    </h2>
                    <div className="w-16 h-1 bg-primary rounded-full mb-6"></div>
                  </div>

                  <div className="p-6 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                    <div className="flex items-center mb-4">
                      <Icon name="Target" size={28} className="text-primary mr-3" />
                      <span className="text-lg font-semibold text-muted-foreground">2025/2026</span>
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
                      "Rise to be Strong"
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      To achieve this, I have set up a year plan with various projects to focus on existing members' integrity and to identify devoted young people who realize the power of action.
                    </p>
                    <p className="text-foreground font-semibold">
                      We will raise the standards, not alone, but together.
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <p className="font-semibold text-foreground">Leo Vidurshanan Shakthi</p>
                      <p className="text-sm text-primary">Club President</p>
                    </div>
                    <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-primary/20">
                      <img
                        src="/assets/images/members/cp_profile.jpg"
                        alt="Leo Vidurshanan Shakthi"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PresidentMessage;